import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { db } from "~/db/db";
import { useGetGvFaturamento } from "~hooks/useSincronizacao";
import { faturamentoMock } from "../mocks/new/Faturamento.mock";
import useVendedor from "~fetchs/useVendedor";
import { chaveMes, chaveMesAnterior, fmtPct, meses as nomesMeses, mesParaData, nomeProdutoLimpo } from "../utils/formatters";
import { extractUpdatedAt } from "../utils/extractUpdatedAt";
import {
  createColorAssigner,
  getGrupoMaterialColor,
} from "../../RouteOptimizationV2/components/Graficos/chartColors";
import { usarMock } from "../utils/dataSource";

const mesesAbrevMai = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];

const allOption = { value: "todos", label: "TODOS" };

const fmtVol = (v) =>
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v ?? 0);

const calcPctVar = (atual, anterior) =>
  anterior > 0 ? fmtPct(((atual - anterior) / anterior) * 100) : null;

const mergeCards = (items) => {
  const porChave = new Map();

  for (const item of items) {
    for (const card of item.cards ?? []) {
      const chave = `${card.inicioMes}||${card.prodHier3 ?? ""}||${card.prodHier5 ?? ""}`;
      if (!porChave.has(chave)) {
        porChave.set(chave, {
          inicioMes: card.inicioMes,
          prodHier3: card.prodHier3,
          prodHier5: card.prodHier5,
          volumeD1: 0,
          volumeMesAtual: 0,
          volumeProjetado: 0,
          volumeMeta: 0,
          createdAtFat: null,
        });
      }

      const e = porChave.get(chave);
      e.volumeD1 += card.volumeD1 ?? 0;
      e.volumeMesAtual += card.volumeMesAtual ?? 0;
      e.volumeProjetado += card.volumeProjetado ?? 0;
      e.volumeMeta += card.volumeMeta ?? 0;
      if (!e.createdAtFat || card.createdAtFat > e.createdAtFat) e.createdAtFat = card.createdAtFat;
    }
  }

  return [...porChave.values()];
};
const flattenVendedorFaturamento = (rawArr, vendedorSelecionado) => {
  if (!rawArr || !Array.isArray(rawArr) || !rawArr.length) return rawArr;
  if (!rawArr.some((v) => Array.isArray(v?.basesFaturamento))) return rawArr;

  const filtered = vendedorSelecionado != null
    ? rawArr.filter((v) => v?.id === vendedorSelecionado)
    : rawArr;

  const stampListagem = (v) =>
    (v?.basesFaturamento ?? []).map((base) =>
      base?.id === 5
        ? {
            ...base,
            listagem: (base.listagem ?? []).map((l) => ({
              ...l,
              codigoVendedor: v.codigoVendedor,
              nomeVendedor: v.nomeVendedor,
            })),
          }
        : base,
    );

  const all = filtered.flatMap(stampListagem);
  if (filtered.length <= 1) return all;

  const byId = new Map();
  for (const item of all) {
    if (!byId.has(item.id)) byId.set(item.id, []);
    byId.get(item.id).push(item);
  }

  const getById = (id) => byId.get(id) ?? [];
  const concatField = (id, key) => {
    const items = getById(id);
    return items.length ? { id, [key]: items.flatMap((i) => i[key] ?? []) } : null;
  };

  const filtrosMerged = getById(6).length
    ? {
        id: 6,
        filtros: [
          ...new Map(
            getById(6)
              .flatMap((i) => i.filtros ?? [])
              .map((f) => [`${f.grupoProduto}|${f.produto}`, f])
          ).values(),
        ],
      }
    : null;

  return [
    getById(1).length ? { id: 1, cards: mergeCards(getById(1)) } : null,
    concatField(2, 'volumes'),
    concatField(3, 'faturamentos'),
    concatField(4, 'positivacoes'),
    concatField(5, 'listagem'),
    filtrosMerged,
  ].filter(Boolean);
};

const emptyDados = { cards: [], volumes: [], faturamentos: [], positivacoes: [], listagem: [], filtrosRaw: [] };

const extrairDados = (source) => {
  const dataArr = Array.isArray(source) && source.every((d) => typeof d?.id === 'number')
    ? source
    : source?.[0]?.[0]?.data?.[0] ?? null;

  if (!dataArr) return emptyDados;

  const byId = new Map(dataArr.map((d) => [d.id, d]));
  return {
    cards:        byId.get(1)?.cards        ?? [],
    volumes:      byId.get(2)?.volumes      ?? [],
    faturamentos: byId.get(3)?.faturamentos ?? [],
    positivacoes: byId.get(4)?.positivacoes ?? [],
    listagem:     byId.get(5)?.listagem     ?? [],
    filtrosRaw:   byId.get(6)?.filtros      ?? [],
  };
};


const buildFilterOptions = (filtrosRaw) => {

  const grupos = new Set();
  const produtos = new Set();
  const produtoToGrupo = new Map();

  for (const f of filtrosRaw) {
    if (f?.grupoProduto) grupos.add(f.grupoProduto);
    if (f?.produto) {
      produtos.add(f.produto);
      if (f.grupoProduto) produtoToGrupo.set(f.produto, f.grupoProduto);
    }
  }

  const toOpts = (set) => [allOption, ...[...set].sort().map((v) => ({ value: v, label: v }))];

  return {
    grupoOptions:   toOpts(grupos),
    produtoOptions: toOpts(produtos),
    produtoToGrupo,
  };
};


const filtrarCards = (linhas, grupoProduto, produto) =>
  linhas.filter(
    (c) =>
      (grupoProduto === "todos" || c.prodHier3 === grupoProduto) &&
      (produto === "todos" || nomeProdutoLimpo(c.prodHier5) === nomeProdutoLimpo(produto)),
  );

const somaCards = (linhas) => {

  const metaPorGrupo = new Map();
  const total = linhas.reduce(
    (acc, c) => {
      if (!metaPorGrupo.has(c.prodHier3)) metaPorGrupo.set(c.prodHier3, c.volumeMeta ?? 0);
      return {
        volumeD1: acc.volumeD1 + (c.volumeD1 ?? 0),
        volumeMesAtual: acc.volumeMesAtual + (c.volumeMesAtual ?? 0),
        volumeProjetado: acc.volumeProjetado + (c.volumeProjetado ?? 0),
      };
    },
    { volumeD1: 0, volumeMesAtual: 0, volumeProjetado: 0 },
  );

  return {
    ...total,
    volumeMeta: [...metaPorGrupo.values()].reduce((a, b) => a + b, 0),
  };
};

const mesesDosCards = (cardsRaw) =>
  [...new Set(cardsRaw.map((c) => c.inicioMes).filter(Boolean))].sort();

const mesAlvoDosCards = (meses, mes) => {
  if (!meses.length) return null;
  if (!mes) return meses[meses.length - 1];
  const [mm, yyyy] = mes.split("/");
  return meses.find((m) => m.startsWith(`${yyyy}-${mm}-`)) ?? null;
};

const normalizarGrupo = (nome) => String(nome ?? "").trim().toUpperCase();

const buildGovs = (cardsRaw, mes, gruposMateriais) => {
  const mesAlvo = mesAlvoDosCards(mesesDosCards(cardsRaw), mes);
  if (!mesAlvo) return [];

  const metaPorGrupo = new Map();
  for (const c of cardsRaw) {
    if (c.inicioMes !== mesAlvo) continue;
    const grupo = normalizarGrupo(c.prodHier3);
    const meta = c.volumeMeta ?? 0;
    if (meta > (metaPorGrupo.get(grupo) ?? 0)) metaPorGrupo.set(grupo, meta);
  }

  return gruposMateriais
    .map((g) => ({
      grupoMaterialId: g.id,
      nomeGrupoMaterial: g.nome,
      metaGov: metaPorGrupo.get(normalizarGrupo(g.nome)) ?? 0,
    }))
    .filter((g) => g.metaGov > 0);
};

const tituloVolumeMes = (mesAlvo, hoje = new Date()) => {
  const [ano, mes] = chaveMes(mesAlvo).split("-").map(Number);
  if (ano === hoje.getFullYear() && mes === hoje.getMonth() + 1) return "Volume Mês Atual";
  return `Volume Mês ${nomesMeses[mes - 1]}`;
};

const buildCards = (cardsRaw, mes, { grupoProduto = "todos", produto = "todos" } = {}) => {
  if (!cardsRaw.length) return [];

  const meses = mesesDosCards(cardsRaw);
  const mesAlvo = mesAlvoDosCards(meses, mes);
  if (!mesAlvo) return [];

  const chaveAnterior = chaveMesAnterior(mesAlvo);
  const mesAnterior = meses.find((m) => chaveMes(m) === chaveAnterior) ?? null;

  const totalDoMes = (alvo) =>
    somaCards(filtrarCards(cardsRaw.filter((c) => c.inicioMes === alvo), grupoProduto, produto));

  const atual = totalDoMes(mesAlvo);
  const anterior = mesAnterior ? totalDoMes(mesAnterior) : null;

  const vsM1 = (valor, base, sufixo = "vs M-1") => {
    const pct = valor ? calcPctVar(valor, base ?? 0) : null;
    return `${pct ?? "—"} ${sufixo}`;
  };

  return [
    {
      title: "Volume D-1:",
      value: `${fmtVol(atual.volumeD1)} t`,
      color: "navy",
    },
    {
      title: tituloVolumeMes(mesAlvo),
      value: `${fmtVol(atual.volumeMesAtual)} t`,
      subtitle: vsM1(atual.volumeMesAtual, anterior?.volumeMesAtual),
      color: "green",
    },
    {
      title: "Volume Projetado",
      value: `${fmtVol(atual.volumeProjetado)} t`,
      subtitle: vsM1(atual.volumeProjetado, anterior?.volumeMesAtual),
      color: "amber",
    },
    {
      title: "Volume Meta",
      value: `${fmtVol(atual.volumeMeta)} t`,
      subtitle: `${calcPctVar(atual.volumeProjetado, atual.volumeMeta) ?? "—"} vs Projetado`,
      color: "blue",
    },
  ];
};


const buildGraficoData = (volumes, faturamentos) => {

  const volumeMap = new Map();
  for (const v of volumes) {
    const key = `${v.inicioMes}||${v.prodHier3}||${v.prodHier5}||${v.prodHier6}`;
    volumeMap.set(key, { volume: v.volume ?? 0, volumeDu: v.volumeDu ?? 0 });
  }


  const grupoIds = new Map();
  let nextGrupoId = 1;
  const getGrupoId = (nome) => {
    if (!grupoIds.has(nome)) grupoIds.set(nome, nextGrupoId++);
    return grupoIds.get(nome);
  };

  const produtoIds = new Map();
  let nextProdutoId = 1;
  const getProdutoId = (grupoId, nome) => {
    const key = `${grupoId}::${nome}`;
    if (!produtoIds.has(key)) produtoIds.set(key, nextProdutoId++);
    return produtoIds.get(key);
  };

  const faturamentosData = faturamentos.map((fat) => {
    const key = `${fat.inicioMes}||${fat.prodHier3}||${fat.prodHier5}||${fat.prodHier6}`;
    const { volume: vol, volumeDu } = volumeMap.get(key) ?? { volume: 0, volumeDu: 0 };
    const grupoId = getGrupoId(fat.prodHier3);
    const produtoId = getProdutoId(grupoId, nomeProdutoLimpo(fat.prodHier5));
    return {
      clienteId: 1,
      codigoCliente: "001",
      nomeCliente: "Carteira",
      grupoMaterialId: grupoId,
      nomeGrupoMaterial: fat.prodHier3,
      produtoId,
      codigoProduto: `P${String(produtoId).padStart(3, "0")}`,
      nomeProduto: nomeProdutoLimpo(fat.prodHier5),
      data: fat.inicioMes,
      volume: vol,
      volumeDu,
      faturamentoBruto: fat.faturamento,
      precoFobLiquidoTotal: vol > 0 ? fat.faturamento / vol : 0,
    };
  });

  const filtros = {
    gruposMateriais: Array.from(grupoIds.entries()).map(([nome, id]) => ({ id, nome })),
    produtos: Array.from(produtoIds.entries()).map(([key, id]) => {
      const sep = key.indexOf("::");
      const grupoId = Number(key.slice(0, sep));
      const nome = key.slice(sep + 2);
      return { id, grupoMaterialId: grupoId, codigo: `P${String(id).padStart(3, "0")}`, nome };
    }),
  };

  return { faturamentosData, filtros };
};

const selecionarJanelaPositivacao = (mesesOrdenados, mesSelecionado) => {
  if (!mesesOrdenados.length) return mesesOrdenados;

  const chaveMes = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const porChave = new Map(mesesOrdenados.map((m) => [chaveMes(new Date(m)), m]));
  const base = mesSelecionado ?? new Date(mesesOrdenados[mesesOrdenados.length - 1]);

  return [12, 5, 4, 3, 2, 1, 0]
    .map((offset) => porChave.get(chaveMes(new Date(base.getFullYear(), base.getMonth() - offset, 1))))
    .filter(Boolean);
};

const buildPositivacoes = (positivacoes, mesSelecionado) => {
  if (!positivacoes.length) return { labels: [], datasets: [] };

  const totalPorMes = new Map();
  const countPorMesGrupo = new Map();
  const mesesSet = new Set();
  const gruposSet = new Set();

  for (const p of positivacoes) {
    const { inicioMes, prodHier3 } = p;
    const quantidade = p.quantidade ?? 0;
    mesesSet.add(inicioMes);
    gruposSet.add(prodHier3);
    totalPorMes.set(inicioMes, (totalPorMes.get(inicioMes) ?? 0) + quantidade);
    if (!countPorMesGrupo.has(inicioMes)) countPorMesGrupo.set(inicioMes, new Map());
    const grupoMap = countPorMesGrupo.get(inicioMes);
    grupoMap.set(prodHier3, (grupoMap.get(prodHier3) ?? 0) + quantidade);
  }

  const mesesOrdenados = selecionarJanelaPositivacao([...mesesSet].sort(), mesSelecionado);
  const labels = mesesOrdenados.map((m) => {
    const d = new Date(m);
    return `${mesesAbrevMai[d.getMonth()]}/${d.getFullYear()}`;
  });

  const grupos = [...gruposSet];
  const assignColor = createColorAssigner();
  const datasets = grupos.map((grupo) => {
    const quantidades = mesesOrdenados.map((mes) => countPorMesGrupo.get(mes)?.get(grupo) ?? 0);
    return {
      label: grupo.toUpperCase(),
      data: mesesOrdenados.map((mes, idx) => {
        const total = totalPorMes.get(mes) ?? 0;
        if (total === 0) return 0;
        return Math.round((quantidades[idx] / total) * 100);
      }),
      quantidades,
      backgroundColor: getGrupoMaterialColor(grupo) ?? assignColor(grupo),
      stack: "stack",
    };
  });

  return { labels, datasets };
};


const gruposDoCliente = (item) => item?.gruposProduto ?? item?.produtos ?? [];

const camposVazios = () => ({
  volumeAnterior: 0, volumeAtual: 0, projetado: 0, positivacaoM1: 0, positivacaoM0: 0,
});

const somaValores = (arr) =>
  arr.reduce((acc, v) => ({
    volumeAnterior: acc.volumeAnterior + (v.volumeAnterior ?? 0),
    volumeAtual:    acc.volumeAtual    + (v.volumeAtual    ?? 0),
    projetado:      acc.projetado      + (v.projetado      ?? 0),
    positivacaoM1:  acc.positivacaoM1  + (v.positivacaoM1  ?? 0),
    positivacaoM0:  acc.positivacaoM0  + (v.positivacaoM0  ?? 0),
  }), camposVazios());

const toRow = (nome, v) => {
  const ant  = v.volumeAnterior ?? 0;
  const atu  = v.volumeAtual    ?? 0;
  const proj = v.projetado      ?? 0;
  const m1   = v.positivacaoM1  ?? 0;
  const m0   = v.positivacaoM0  ?? 0;
  return {
    nome,
    mesAnterior: fmtVol(ant),
    mesAtual: fmtVol(atu),
    projetado: fmtVol(proj),
    pctMesAntXMesAtual: calcPctVar(atu, ant),
    pctVarProjXMesAnt: calcPctVar(proj, ant),
    positivacaoM1: m1,
    positivacaoM0: m0,
    pctVariacao: calcPctVar(m0, m1),
  };
};


const listagemDoMes = (listagem, mes) => {
  const meses = [...new Set(listagem.map((i) => i.inicioMes).filter(Boolean))].sort();
  if (!meses.length) return listagem;

  let alvo = meses[meses.length - 1];
  if (mes) {
    const [mm, yyyy] = mes.split("/");
    const encontrado = meses.find((m) => m.startsWith(`${yyyy}-${mm}-`));
    alvo = encontrado ?? null;
  }
  return alvo ? listagem.filter((i) => i.inicioMes === alvo) : [];
};

const buildRows = (listagem, { grupoFiltro = "todos", produtoFiltro = "todos" } = {}) => {
  const vendedorMap = new Map();
  const temFiltro = grupoFiltro !== "todos" || produtoFiltro !== "todos";

  const buildGrupos = (produtosArr) => {
    let grupos = produtosArr ?? [];
    if (grupoFiltro !== "todos") grupos = grupos.filter((g) => g.produto === grupoFiltro);

    return grupos.reduce((acc, g) => {
      let hier5 = g.produtos ?? [];
      if (produtoFiltro !== "todos") {
        hier5 = hier5.filter((p) => p.produto === produtoFiltro);
        if (!hier5.length) return acc;
      }

      const valores = produtoFiltro !== "todos" ? somaValores(hier5) : g;
      const row = toRow(g.produto, valores);


      if (hier5.length) row.produtos = hier5.map((p) => toRow(p.produto, p));

      acc.push({ row, valores });
      return acc;
    }, []);
  };

  for (const item of listagem) {
    const grupos = buildGrupos(gruposDoCliente(item));
    if (temFiltro && !grupos.length) continue;

    const key = item.codigoVendedor;
    if (!vendedorMap.has(key)) {
      vendedorMap.set(key, {
        id: key,
        vendedor: item.nomeVendedor,
        acc: camposVazios(),
        clientes: [],
      });
    }

    const valores = temFiltro ? somaValores(grupos.map((g) => g.valores)) : item;

    const v = vendedorMap.get(key);
    v.acc = somaValores([v.acc, valores]);

    v.clientes.push({
      ...toRow(item.razaoSocial, valores),
      id: item.clienteId,
      codigoCliente: item.codigoCliente,
      grupoCentralizador: item.grupoCentralizador ?? null,
      grupos: grupos.map((g) => g.row),
    });
  }

  return Array.from(vendedorMap.values()).map(({ id, vendedor, acc, clientes }) => ({
    ...toRow(vendedor, acc),
    id,
    codigoVendedor: id,
    vendedor,
    clientes,
  }));
};


const extractItemsArray = (response) => {
  const dataArr =
    response?.data?.data?.[0] ??
    response?.data?.[0] ??
    response?.[0] ??
    null;
  return Array.isArray(dataArr) ? dataArr : null;
};

export const useFatsData = (
  mes,
  { grupoProduto = "todos", produto = "todos", grupoMaterialId = 0, produtoId = 0 } = {},
) => {
  const { vendedorSelecionado } = useVendedor();
  const [rawItems, setRawItems] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const hasMock = usarMock(faturamentoMock);

  const { data: queryData, isFetching, isRefetching, isError } =
    useGetGvFaturamento(!hasMock && navigator.onLine, 0, 0, null);

  const apiItems = useMemo(() => {
    if (hasMock) return faturamentoMock?.[0]?.[0]?.data?.[0] ?? null;
    return extractItemsArray(queryData);
  }, [hasMock, queryData]);

  const loadFromDexie = useCallback(async () => {
    try {
      const rows = await db.gvFaturamento.toArray();
      if (rows?.length) {
        const stored = rows[0]?.payload;
        if (stored) setRawItems(stored);
      }
    } catch (err) {
      console.error("Erro ao carregar gvFaturamento do Dexie:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);


  useEffect(() => {
    if (hasMock) {
      setRawItems(faturamentoMock?.[0]?.[0]?.data?.[0] ?? null);
      setIsLoading(false);
      return;
    }
    loadFromDexie();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const prevVendedor = useRef(undefined);
  useEffect(() => {
    if (vendedorSelecionado === undefined) return;
    if (prevVendedor.current === undefined) {
      prevVendedor.current = vendedorSelecionado;
      return;
    }
    if (prevVendedor.current === vendedorSelecionado) return;
    prevVendedor.current = vendedorSelecionado;
    if (!hasMock) {
      setRawItems(null);
      loadFromDexie();
    }
  }, [vendedorSelecionado, hasMock, loadFromDexie]);

  useEffect(() => {
    if (hasMock || !navigator.onLine) return;

    if (isFetching || isRefetching) {
      setIsRefreshing(true);
      return;
    }

    setIsRefreshing(false);

    if (!isError && apiItems != null) {
      setRawItems(apiItems);
      setIsLoading(false);
      db.gvFaturamento.clear()
        .then(() => db.gvFaturamento.add({ payload: apiItems, syncedAt: new Date().toISOString() }))
        .catch((err) => console.error("Erro ao salvar gvFaturamento no Dexie:", err));
    }
  }, [apiItems, hasMock, isError, isFetching, isRefetching]);


  useEffect(() => {
    const handleOffline = () => {
      if (!hasMock) {
        setIsRefreshing(false);
        loadFromDexie();
      }
    };
    window.addEventListener("offline", handleOffline);
    return () => window.removeEventListener("offline", handleOffline);
  }, [hasMock, loadFromDexie]);

  const normalizedItems = useMemo(
    () => flattenVendedorFaturamento(rawItems, vendedorSelecionado),
    [rawItems, vendedorSelecionado]
  );

  return useMemo(() => {
    const { cards: cardsRaw, volumes, faturamentos, positivacoes, listagem, filtrosRaw } = extrairDados(normalizedItems);

    const { faturamentosData, filtros } = buildGraficoData(volumes, faturamentos);
    const positivacoesChartData = buildPositivacoes(positivacoes, mesParaData(mes));
    const filterOptions = buildFilterOptions(filtrosRaw);

    const nomeDoId = (lista, id) => (id ? (lista.find((i) => i.id === id)?.nome ?? "todos") : null);
    const grupoCards = nomeDoId(filtros.gruposMateriais, grupoMaterialId) ?? grupoProduto;
    const produtoCards = nomeDoId(filtros.produtos, produtoId) ?? produto;

    const volumeCards = buildCards(cardsRaw, mes, {
      grupoProduto: grupoCards,
      produto: produtoCards,
    });


    const govs = buildGovs(cardsRaw, null, filtros.gruposMateriais);

    let grupoFiltro = grupoProduto;
    if (produto !== "todos") {
      const grupoDoProduto = filterOptions.produtoToGrupo.get(produto);
      if (grupoDoProduto && (!grupoFiltro || grupoFiltro === "todos")) {
        grupoFiltro = grupoDoProduto;
      }
    }

    const listagemDaCompetencia = listagemDoMes(listagem, mes);

    const listagemFiltrada =
      grupoFiltro !== "todos"
        ? listagemDaCompetencia.filter((item) => gruposDoCliente(item).some((g) => g.produto === grupoFiltro))
        : listagemDaCompetencia;

    const listRows = buildRows(listagemFiltrada, { grupoFiltro, produtoFiltro: produto });

    const updatedAt = extractUpdatedAt(rawItems);

    const mesesDeCards = [...new Set(cardsRaw.map((c) => c.inicioMes).filter(Boolean))];
    const mesesDeListagem = [...new Set(listagem.map((i) => i.inicioMes).filter(Boolean))];

    return { volumeCards, govs, faturamentosData, filtros, positivacoesChartData, listRows, filterOptions, updatedAt, mesesDeCards, mesesDeListagem, isLoading, isRefreshing };
  }, [normalizedItems, rawItems, mes, grupoProduto, produto, grupoMaterialId, produtoId, isLoading, isRefreshing]);
};
